// Create/Edit Job page
import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../atoms";
import { FormField } from "../molecules";
import { AdminLayout } from "../organisms";
import { jobService } from "../../services/jobService";

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export const CreateEditJob = () => {
  const navigate = useNavigate();
  const redirectTimer = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contactNumber: "",
    description: "",
    jobType: "Requested",
  });
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Clear the redirect timer if the user navigates away before it fires.
  useEffect(
    () => () => {
      if (redirectTimer.current) clearTimeout(redirectTimer.current);
    },
    [],
  );

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);

    const oversized = files.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (oversized.length) {
      setError(`Each image must be ${MAX_FILE_SIZE_MB} MB or smaller`);
      return;
    }

    if (files.length + images.length > 5) {
      setError("Maximum 5 images allowed");
      return;
    }
    setImages((prev) => [...prev, ...files]);
    setError("");
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.email.trim()) {
      setError("Email is required");
      return;
    }

    if (!formData.contactNumber.trim()) {
      setError("Contact number is required");
      return;
    }

    if (!formData.description.trim()) {
      setError("Description is required");
      return;
    }

    try {
      setLoading(true);
      await jobService.createJob(
        formData.name.trim(),
        formData.email.trim(),
        formData.contactNumber.trim(),
        formData.description.trim(),
        images,
        formData.jobType,
      );
      setSuccess("Job created successfully!");
      setFormData({
        name: "",
        email: "",
        contactNumber: "",
        description: "",
        jobType: "Requested",
      });
      setImages([]);
      redirectTimer.current = setTimeout(() => {
        navigate("/admin/jobs");
      }, 2000);
    } catch (err) {
      setError(err.message || "Failed to create job");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="Create New Job">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-soft border border-terracotta-100/60 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <FormField
              label="Name"
              type="text"
              placeholder="Enter requester's name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
            />

            <FormField
              label="Email"
              type="email"
              placeholder="Enter email address"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              required
            />

            <FormField
              label="Contact Number"
              type="tel"
              placeholder="Enter contact number"
              value={formData.contactNumber}
              onChange={(e) =>
                setFormData({ ...formData, contactNumber: e.target.value })
              }
              required
            />

            <div>
              <label className="block text-sm font-semibold mb-2 text-ink-soft">
                Job Type
              </label>
              <select
                value={formData.jobType}
                onChange={(e) =>
                  setFormData({ ...formData, jobType: e.target.value })
                }
                className="w-full px-4 py-3 border border-terracotta-100/60 rounded-lg focus:outline-none focus:border-terracotta-500"
              >
                <option value="Requested">General Request</option>
                <option value="HomeRepair">Home Repair</option>
                <option value="CarRepair">Car Repair</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-ink-soft">
                Description
              </label>
              <textarea
                placeholder="Enter job description"
                rows="6"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="w-full px-4 py-3 border border-terracotta-100/60 rounded-lg focus:outline-none focus:border-terracotta-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-ink-soft">
                Attach Images (optional, max 5)
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                  id="job-file-upload"
                  disabled={images.length >= 5}
                />
                <label
                  htmlFor="job-file-upload"
                  className={`flex items-center justify-center w-full px-6 py-4 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
                    images.length >= 5
                      ? "border-terracotta-200 bg-cream cursor-not-allowed opacity-60"
                      : "border-terracotta-400 bg-terracotta-50 hover:bg-terracotta-100 hover:border-terracotta-500"
                  }`}
                >
                  <div className="text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-terracotta-500"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <p className="mt-2 text-sm text-ink-soft">
                      {images.length >= 5 ? (
                        <span className="font-semibold text-ink-soft">
                          Maximum images reached
                        </span>
                      ) : (
                        <>
                          <span className="font-semibold text-terracotta-600">
                            Click to upload
                          </span>
                          <span className="text-ink-soft">
                            {" "}
                            or drag and drop
                          </span>
                        </>
                      )}
                    </p>
                    <p className="mt-1 text-xs text-ink-soft">
                      PNG, JPG, GIF up to 10MB ({images.length}/5 images)
                    </p>
                  </div>
                </label>
              </div>
              {images.length > 0 && (
                <div className="mt-4 space-y-2">
                  {images.map((image, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-white p-3 rounded-lg border border-terracotta-100/60 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center flex-1 min-w-0">
                        <svg
                          className="h-5 w-5 text-terracotta-500 mr-2 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <span className="text-sm text-ink truncate">
                          {image.name}
                        </span>
                        <span className="ml-2 text-xs text-ink-soft flex-shrink-0">
                          ({(image.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="ml-3 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-full transition-colors flex-shrink-0"
                        aria-label="Remove image"
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                {success}
              </div>
            )}

            <div className="flex gap-4">
              <Button
                variant="primary"
                type="submit"
                disabled={loading}
                className="flex-1"
              >
                {loading ? "Creating..." : "Create Job"}
              </Button>
              <Link to="/admin/jobs" className="flex-1">
                <Button variant="secondary" className="w-full">
                  Cancel
                </Button>
              </Link>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};
